import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Flex, Form, Select, Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { IBrand, useBrandsSlice } from 'entities/Brand';
import { INotificationSource } from 'entities/Notification/model/types';
import { IOffer, OfferCardJoin } from 'entities/Offer';
import { EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { sendYmEvent } from 'shared/utils';

const { Title, Text } = Typography;

interface IJoinFormProps extends HTMLAttributes<HTMLDivElement> {
	brand: IBrand;
	offer?: IOffer;
	source?: INotificationSource;
	isLoading: boolean;
	sourceOptions: { value: string; label: string }[];
	type?: 'brand' | 'offer';
	isInvite?: boolean;
	onJoin: (params: { uuid: string; body: string[] }) => void;
	onClose: () => void;
}

export const JoinForm: FC<IJoinFormProps> = ({
	brand,
	offer,
	source,
	isLoading,
	sourceOptions,
	type = 'brand',
	isInvite,
	onJoin,
	onClose,
}) => {
	const { t } = useTranslation();
	const { setJoinedSources } = useBrandsSlice();

	const initialValues: { sources: string[]; terms: boolean } = { sources: source ? [source.uuid] : [], terms: false };
	const validationSchema = Yup.object({
		sources: Yup.array().min(1, t('Выберите хотя бы одну площадку')),
		terms: Yup.bool().isTrue(t('Необходимо принять условие')),
	});

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={({ sources }) => {
				sendYmEvent({
					event: EYmEvents.CLICK_SEND_JOIN,
					o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.CATALOG],
					o2: EYmEventsO2.JOIN,
					pi: brand.uuid,
					wi: sources.join(';'),
				});
				onJoin({ uuid: offer ? offer.uuid : brand.uuid, body: sources });
			}}>
			{({ values, errors, touched, handleSubmit, getFieldProps, setFieldValue, setFieldTouched }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Flex vertical gap={24}>
							<Flex vertical gap={12}>
								<img src={brand.image} alt={brand.name} width={80} />
								<Title level={4} style={{ margin: 0 }}>
									{brand.name}
								</Title>
							</Flex>

							<Text type="secondary" style={{ fontSize: 16 }}>
								{type === 'brand' &&
									t(
										'Заявка подается на все офферы рекламодателя. Внимательно ознакомьтесь с их правилами. В случае нарушения правил ваша площадка может быть отключен от оффера.',
									)}

								{type === 'offer' &&
									t(
										'Внимательно ознакомьтесь с их правилами. В случае нарушения правил ваша площадка может быть отключен от оффера.',
									)}
							</Text>
							<Flex vertical gap={8}>
								<Title level={5} style={{ margin: 0 }}>
									{!isInvite && t('Площадки на подключение')}
									{isInvite && t('Площадка на подключение')}
								</Title>

								<Form.Item
									validateStatus={errors.sources && touched.sources ? 'error' : ''}
									help={
										errors.sources && touched.sources
											? errors.sources
											: !isInvite
												? t('В одной заявке вы можете подключить сразу несколько площадок.')
												: ''
									}>
									<Select
										mode="multiple"
										placeholder={t('Выберите площадки')}
										options={sourceOptions}
										showSearch
										optionFilterProp="label"
										allowClear
										disabled={isLoading || (isInvite && !!source)}
										{...getFieldProps('sources')}
										value={values.sources}
										onChange={(value) => {
											setFieldValue('sources', value);
											setJoinedSources(value);
										}}
										onBlur={() => setFieldTouched('sources')}
										getPopupContainer={(trigger) => trigger.parentElement}
									/>
								</Form.Item>
							</Flex>

							<Flex vertical gap={8}>
								<Title level={5} style={{ margin: 0 }}>
									{type === 'brand' && t('Офферы рекламодателя')}
									{type === 'offer' && t('Оффер рекламодателя')}
								</Title>

								{(offer ? [offer] : brand.offers).map((offer) => (
									<OfferCardJoin key={offer.uuid} offer={offer} brand={brand} />
								))}
							</Flex>

							<Form.Item
								validateStatus={errors.terms && touched.terms ? 'error' : ''}
								help={errors.terms && touched.terms && errors.terms}>
								<Checkbox {...getFieldProps('terms')} disabled={isLoading}>
									{t(
										'Я принимаю Правила программ, указанных в деталях офферов, и подтверждаю, что прочитал и понял их.',
									)}
								</Checkbox>
							</Form.Item>

							<Flex justify="space-between" style={{ marginTop: 20 }}>
								<Button onClick={onClose}>{t('Отменить')}</Button>

								<Button
									type="primary"
									htmlType="submit"
									loading={isLoading}
									disabled={isLoading || !!Object.keys(errors).length || !values.sources.length || !values.terms}>
									{t('Подать заявку')}
								</Button>
							</Flex>
						</Flex>
					</form>
				);
			}}
		</Formik>
	);
};
